import React from 'react';
import { Button, ClampText, EmptyValue, Form, RowActions, Table } from '@/components/ui';
import { formatDate } from '@/utils/displayDateTime';
import InfoTooltip from '../../../components/InfoTooltip';
import type {
  HiredEquipmentGridProps,
  HiredEquipmentGridRow,
  PurchaseOrderOption,
  ResourceOption,
  TaskOption,
} from '../types';
import { LUMP_SUM_RESOURCE_LABEL } from '../types';
import {
  INTERNAL_MATERIALS_ARIA,
  INTERNAL_MATERIALS_BADGE,
  INTERNAL_MATERIALS_COLUMN,
  INTERNAL_MATERIALS_NOTES_PLACEHOLDER,
  INTERNAL_MATERIALS_TOOLTIP,
} from '../config/internalMaterialsCopy';
import { useHiredEquipmentFieldVisibility } from '../hooks/useHiredEquipmentFieldVisibility';
import { defaultFuelSurchargeEligible, defaultFuelSurchargeEligibleFromVehicleType } from '../utils/fuelSurchargeDefault';
import { fuelSurchargePerHourForLine, lookupFuelSurchargePerHour } from '../utils/fuelSurchargeLookup';
import { getGridRowLocks } from '../utils/gridLocks';
import { formatCurrency, lineAmount, sumAmounts } from '../utils/gridMath';
import {
  fuelOverrideProblem,
  gridLineElementId,
  HE_LUMP_SUM_DESCRIPTION_MAX,
  HE_MAX_HOURS_PER_DAY,
  HE_SAME_DAY_WARN_HOURS,
  isRequiredFieldMissing,
  requiredFieldMessage,
  qtyStepForUnit,
  quantityProblem,
  rowHasMissingRequired,
  validateGridRows,
  type RequiredGridField,
} from '../utils/gridValidation';
import { formatRateWindow } from '../hooks/useHeLinePricing';
import LineCommentCell from './LineCommentCell';
import { hasHePaperTicket } from '../utils/heSummaryRows';
import {
  canToggleHeOvertime,
  defaultOvertimeEligible,
  formatHeOvertimeBreakdownText,
  formatHeOvertimeFooterText,
} from '../utils/overtime';
import { formatTaskLabel, isStandingRoutineTask, normalizeLumpSumRow } from '../utils/poPrefill';
import { formatPoOptionLabel } from '../utils/poDisplay';
import { filterPosForTask, nextRowAfterPoChange, nextRowAfterTaskChange, scopedTasksForPoRow } from '../utils/poTaskFilter';
import HiredEquipmentTaskPicker from './HiredEquipmentTaskPicker';
import RowAttachmentCell from './RowAttachmentCell';

const FUEL_SURCHARGE_COLUMN = 'Fuel';
const FUEL_SURCHARGE_ARIA = 'Fuel surcharge eligible';
const FUEL_SURCHARGE_TOOLTIP =
  'Fuel surcharge $/hr from the fuels table for this line’s date and vehicle type (On-Road / Off-Road). N/A is $0. Uncheck to exclude it from the amount. A highlighted value is an override; the standard rate is shown beside it.';
const FUEL_SURCHARGE_UNCONFIGURED =
  'Vehicle type is not On-Road or Off-Road – set eligibility explicitly if this line should receive a fuel surcharge.';
const FUEL_SURCHARGE_OVERRIDE_TOOLTIP =
  'Type a fuel surcharge $/hr for this line only ($0.01–$100.00). It does not change Fuel Surcharge or the resource. Leave it empty to use the fuels-table rate.';

const LUMP_SUM_ADD_LABEL = 'Add lump sum row';
const LUMP_SUM_ADD_TOOLTIP =
  'A flat dollar amount on this task – no resource, quantity, rate, fuel surcharge, or overtime. Describe what it pays for and type the amount in the Amount column.';

const OVERTIME_ELIGIBLE_COLUMN = 'OT Eligible';
const OVERTIME_ELIGIBLE_ARIA = 'Overtime eligible';
const OVERTIME_ELIGIBLE_TOOLTIP =
  'Shown when the unit is Hours and the resource is overtime eligible. Hours after 8 (up to 12) bill at 1.5× the rate; hours after 12 bill at 2×. First 8 hours stay at 1×.';

const MoneyHover: React.FC<{ text: string | null; id: string; children: React.ReactNode }> = ({
  text,
  id,
  children,
}) => {
  if (!text) return <>{children}</>;
  return (
    <InfoTooltip content={<span className="tw-whitespace-pre-line">{text}</span>} id={id}>
      {children}
    </InfoTooltip>
  );
};

const CELL =
  'tw-w-full tw-min-w-0 tw-rounded tw-border tw-px-1.5 tw-py-1 tw-text-sm tw-text-content ' +
  'focus:tw-border-line-focus focus:tw-outline-none focus:tw-shadow-focus disabled:tw-bg-surface-alt';

function cellClass(invalid: boolean): string {
  return `${CELL} ${
    invalid
      ? 'tw-border-2 tw-border-danger tw-bg-danger/20 tw-font-medium'
      : 'tw-border-line-input tw-bg-surface'
  }`;
}

function taskDisplay(task: TaskOption): string {
  return formatTaskLabel(task);
}

function posForVendor(purchaseOrders: PurchaseOrderOption[], vendorId: number | null | undefined): PurchaseOrderOption[] {
  if (vendorId == null) return [];
  return purchaseOrders.filter((po) => po.vendorId === vendorId);
}

function resourceOptionEl(resource: ResourceOption) {
  return (
    <option key={resource.id} value={resource.id}>
      {resource.label}
    </option>
  );
}

function ResourceSelectOptions({ options }: { options: ResourceOption[] }) {
  const onPo = options.filter((resource) => resource.onPurchaseOrder === true);
  const other = options.filter((resource) => resource.onPurchaseOrder === false);
  if (onPo.length > 0 && other.length > 0) {
    return (
      <>
        <optgroup label="On this PO">{onPo.map(resourceOptionEl)}</optgroup>
        <optgroup label="Other units">{other.map(resourceOptionEl)}</optgroup>
      </>
    );
  }
  return <>{options.map(resourceOptionEl)}</>;
}

interface CellProps {
  invalid: boolean;
  children: React.ReactNode;
  className?: string;
  /** V2-018: the problem, shown directly under the field after a submit attempt. */
  message?: string | null;
}

/** V2-033: a locked (By Task) Task column is capped at about 240 px. */
const TASK_LOCKED_COL = 'tw-w-[15rem] tw-max-w-[15rem]';
/**
 * V3-031 / TBL-19: long text columns (Vendor, Resource) are capped so Amount stays on screen. A
 * long resource ("Hitachi 2011 ZX 135 US-3 includes one bucket") made its select – and so the
 * column – as wide as the name. The value is cut with "…" and the full text is in its title.
 */
const LONG_TEXT_COL = 'tw-min-w-[9rem] tw-max-w-[13rem]';
const LONG_TEXT_VALUE = 'tw-max-w-[13rem]';

const Td: React.FC<CellProps> = ({ invalid, children, className = '', message }) => (
  <td className={`${invalid ? 'tw-bg-danger/20' : ''} ${className}`} data-invalid={invalid || undefined}>
    {children}
    {invalid && message ? (
      <p role="alert" className="tw-mb-0 tw-mt-0.5 tw-whitespace-normal tw-text-xs tw-font-medium tw-text-danger">
        {message}
      </p>
    ) : null}
  </td>
);

// `tw-block tw-truncate`: locked (read-only) values used to be plain inline spans that would
// wrap onto multiple lines when their column got squeezed (e.g. long vendor/task names at
// higher browser zoom). Because table auto-layout sizes a column from the *narrowest* it can
// get away with, a wrappable cell effectively volunteers its column's width away to neighbors —
// which is exactly what starved the Fuel column down to unreadable. Truncating instead keeps
// every column at the width its header declares (see the `tw-min-w-*` classes below) and pushes
// the overflow into the table's own horizontal scrollbar. `title` restores the full value on
// hover so nothing is actually lost.
const LockedText: React.FC<{ children: React.ReactNode; title?: string; className?: string }> = ({ children, title, className = '' }) => (
  <ClampText lines={1} className={`tw-block tw-min-w-0 tw-flex-1 tw-text-sm tw-text-content ${className}`} title={title}>
    {children || '–'}
  </ClampText>
);

const IntMatCheckFlag: React.FC<{ flagged: boolean }> = ({ flagged }) =>
  flagged ? (
    <span
      aria-hidden="true"
      className="tw-pointer-events-none tw-absolute tw-right-0 tw-top-0 tw-h-0 tw-w-0 tw-border-b-[7px] tw-border-l-0 tw-border-r-[7px] tw-border-t-0 tw-border-b-transparent tw-border-r-primary"
    />
  ) : null;

function parseNumber(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

const HiredEquipmentGrid: React.FC<HiredEquipmentGridProps> = ({
  anchor,
  rows,
  onRowsChange,
  vendors,
  taskOptionForRow,
  searchTasks,
  purchaseOrders,
  tasksByPoId = {},
  resourcesForRow,
  fuelSurcharges = [],
  attemptedSubmit = false,
  onAddLine,
  onAddLumpSumLine,
  onDuplicateLine,
  onRemoveLine,
  onReAddPoResources,
  readOnly = false,
  showAttachmentColumn = true,
  onCreateTask,
  onViewTask,
  onStandingTaskPicked,
  cloningClientIds,
  sameDayHours = [],
  canComment = false,
}) => {
  const { showFuel, canOverrideFuel } = useHiredEquipmentFieldVisibility();
  // Raw text while a numeric cell is being typed in, so "1." or "0.0" are not reformatted mid-entry
  // (the cells are plain text inputs: no spinner arrows, no forced 0).
  const [qtyDraft, setQtyDraft] = React.useState<Record<string, string>>({});
  const [fuelDraft, setFuelDraft] = React.useState<Record<string, string>>({});
  const [amountDraft, setAmountDraft] = React.useState<Record<string, string>>({});
  // V3-024 / EDIT-01: read-only, every cell is label / value text – no disabled inputs.
  const editableLocks = getGridRowLocks(anchor);
  const locks = readOnly
    ? { ...editableLocks, rowVendorSelectable: false, rowPoSelectable: false, rowTaskSelectable: false }
    : editableLocks;
  const showReAdd = anchor.entryMethod === 'purchase_order' && Boolean(onReAddPoResources);
  const rowsWithFuel = rows.map((row) => ({
    ...row,
    fuelPerHour: fuelSurchargePerHourForLine(fuelSurcharges, row),
  }));
  const totalAmount = sumAmounts(rowsWithFuel);
  const footerOtText = formatHeOvertimeFooterText(rowsWithFuel);
  const incompleteCount = attemptedSubmit ? validateGridRows(rows).invalidCount : 0;
  const showTicketColumn = !readOnly || rows.some((row) => hasHePaperTicket(row.ticketNumber));
  const patch = (clientId: string, updates: Partial<HiredEquipmentGridRow>) => {
    // Every call here is a real edit, so any row being patched has now been looked at -
    // clear the "fresh duplicate" shading unconditionally rather than asking each of the
    // dozen call sites below to remember to do it.
    onRowsChange(
      rows.map((row) =>
        row.clientId === clientId
          ? // normalizeLumpSumRow last: a vendor/PO/task change nulls resourceLabel and
            // friends on every row, which would otherwise quietly un-lump this one.
            normalizeLumpSumRow({ ...row, ...updates, duplicatedPending: false })
          : row,
      ),
    );
  };

  const handleVendorMaterialChange = (row: HiredEquipmentGridRow, checked: boolean) => {
    patch(row.clientId, {
      vendorMaterial: checked,
    });
  };

  const invalid = (row: HiredEquipmentGridRow, field: RequiredGridField) =>
    attemptedSubmit && isRequiredFieldMissing(row, field);
  const message = (row: HiredEquipmentGridRow, field: RequiredGridField) =>
    attemptedSubmit ? requiredFieldMessage(row, field) : '';

  const handleVendorChange = (row: HiredEquipmentGridRow, vendorId: number | '') => {
    if (vendorId === '') {
      patch(row.clientId, {
        vendorId: null,
        vendorName: null,
        poId: null,
        poNumber: null,
        resourceId: null,
        resourceLabel: null,
        vehicleType: null,
        fuelSurchargeEligible: null,
        fuelSurchargeOverridePerHour: null,
        overtimeEligible: false,
        resourceOvertimeEligible: false,
      });
      return;
    }
    const vendor = vendors.find((item) => item.id === vendorId);
    patch(row.clientId, {
      vendorId,
      vendorName: vendor?.name ?? null,
      poId: null,
      poNumber: null,
      resourceId: null,
      resourceLabel: null,
      vehicleType: null,
      fuelSurchargeEligible: defaultFuelSurchargeEligible(vendor),
      fuelSurchargeOverridePerHour: null,
      overtimeEligible: false,
      resourceOvertimeEligible: false,
    });
  };

  const handlePoChange = (row: HiredEquipmentGridRow, poId: number | '') => {
    const po = poId === '' ? null : purchaseOrders.find((item) => item.id === poId) ?? null;
    patch(row.clientId, nextRowAfterPoChange(row, po, tasksByPoId, !locks.rowTaskSelectable));
  };

  const lockedTaskOption = (row: HiredEquipmentGridRow): TaskOption | null =>
    taskOptionForRow(row) ?? (row.taskId != null
      ? { id: row.taskId, description: row.taskLabel || `Task ${row.taskId}` }
      : null);

  const handleTaskChange = (row: HiredEquipmentGridRow, task: TaskOption | null) => {
    // C2: a Standing Routine task is a template. Hand it to the page, which opens the create window
    // with it pre-selected; the line gets the new Specific task when that window is saved.
    if (task && onStandingTaskPicked && isStandingRoutineTask(task)) {
      onStandingTaskPicked(row, task);
      return;
    }
    // When the submission is anchored to a PO the row renders it as locked text with no picker, so
    // releasing it here would strand the row with a blank PO and no way to refill it.
    patch(row.clientId, nextRowAfterTaskChange(row, task, tasksByPoId, taskDisplay, locks.poLocked));
  };

  const handleResourceChange = (row: HiredEquipmentGridRow, resourceId: number | '', options: ResourceOption[]) => {
    if (resourceId === '') {
      patch(row.clientId, {
        resourceId: null,
        resourceLabel: null,
        vehicleType: null,
        fuelSurchargeEligible: null,
        fuelSurchargeOverridePerHour: null,
        overtimeEligible: false,
        resourceOvertimeEligible: false,
      });
      return;
    }
    const resource = options.find((item) => item.id === resourceId);
    const unit = resource?.unit ?? row.unit;
    const resourceOt = resource?.overtimeEligible === true;
    patch(row.clientId, {
      resourceId,
      resourceLabel: resource?.label ?? null,
      unit,
      rate: resource?.rate ?? row.rate,
      vehicleType: resource?.vehicleType ?? null,
      fuelSurchargeEligible: defaultFuelSurchargeEligibleFromVehicleType(resource?.vehicleType),
      fuelSurchargeOverridePerHour: null,
      overtimeEligible: defaultOvertimeEligible(resourceOt, unit),
      resourceOvertimeEligible: resourceOt,
      ...(resource?.taskId && !row.taskId
        ? { taskId: resource.taskId, taskLabel: resource.taskLabel ?? row.taskLabel }
        : {}),
    });
  };

  // Final column set (M9 Vendor before PO, L2 "OT eligible", M23 Comment, M28 narrow Fuel, M8 sticky
  // # / Date on the left and Amount / Actions on the right):
  // # Date Vendor PO Task Resource Qty Unit Rate Fuel IntMat OT Comment [Ticket] Attachment | Amount [Actions]
  // 9 lead columns + Int. Mat. + OT + Comment = 12, plus Fuel, Ticket and Attachment when shown.
  const footerLeadColSpan =
    12 + (showFuel ? 1 : 0) + (showTicketColumn ? 1 : 0) + (showAttachmentColumn ? 1 : 0);
  const footerTrailColSpan = readOnly ? 0 : 1;
  const columnCount = footerLeadColSpan + 1 + footerTrailColSpan;

  // M8: sticky edges. Opaque backgrounds so scrolled cells do not show through.
  const STICKY_NUM = 'tw-sticky tw-left-0 tw-z-[2] tw-w-10 tw-min-w-[2.5rem] tw-bg-surface';
  const STICKY_DATE = 'tw-sticky tw-left-[2.5rem] tw-z-[2] tw-min-w-[8.5rem] tw-bg-surface tw-shadow-[inset_-1px_0_0_var(--tw-shadow-color)] tw-shadow-line';
  const STICKY_AMOUNT = `tw-sticky ${readOnly ? 'tw-right-0' : 'tw-right-[5.5rem]'} tw-z-[2] tw-min-w-[7.5rem] tw-bg-surface tw-shadow-[inset_1px_0_0_var(--tw-shadow-color)] tw-shadow-line`;
  const STICKY_ACTIONS = 'tw-sticky tw-right-0 tw-z-[2] tw-w-[5.5rem] tw-min-w-[5.5rem] tw-bg-surface';

  const headerTooltip = (content: string, id: string, label: string) => (
    <InfoTooltip content={content} id={id}>
      <span className="tw-inline-flex tw-items-center tw-gap-1">
        {label}
        <i className="bi bi-info-circle tw-text-content-muted" aria-hidden="true" />
      </span>
    </InfoTooltip>
  );

  return (
    // `tw-flex-shrink-0` matters here: this card sits as a flex item inside the page's
    // bounded-height flex column (HiredEquipmentGridPage / HiredEquipmentDetailedView).
    // Because the card also has `overflow-hidden` (for the rounded corners), its flexbox
    // "automatic minimum size" would otherwise be 0 — so once enough lines were added to
    // exceed the viewport, the flex layout squashed/clipped this card down to fit instead
    // of growing and letting the page's own overflow-y-auto scroll. flex-shrink-0 keeps
    // it at its full natural height so the page scrolls to reveal new lines and Add line.
    <div className="tw-flex-shrink-0 tw-overflow-hidden tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-shadow-card">
      {sameDayHours.length > 0 && (
        <div className="tw-space-y-1 tw-border-b tw-border-line tw-px-4 tw-py-2" role="status">
          {sameDayHours.map((entry) => (
            <p
              key={`${entry.resourceId}-${entry.date}`}
              className={`tw-mb-0 tw-text-sm ${entry.level === 'block' ? 'tw-text-danger' : 'tw-text-warning-text'}`}
            >
              <i className={`bi ${entry.level === 'block' ? 'bi-x-octagon' : 'bi-exclamation-triangle'} tw-mr-1`} aria-hidden="true" />
              {entry.resourceLabel} has {entry.total} hours on {entry.date}
              {entry.otherSubmissions > 0 ? ` (${entry.otherSubmissions} on other submissions)` : ''}
              {entry.level === 'block'
                ? ` – more than ${HE_MAX_HOURS_PER_DAY} hours in one day cannot be submitted.`
                : ` – more than ${HE_SAME_DAY_WARN_HOURS} hours in one day. Check this is right.`}
            </p>
          ))}
        </div>
      )}
      <div className="tw-m-0 tw-min-w-0 tw-border-0 tw-p-0">
      {rows.length === 0 ? (
        <p className="tw-mb-0 tw-px-4 tw-py-10 tw-text-center tw-text-sm tw-text-content-muted">
          {readOnly ? 'No line items on this submission.' : 'No lines yet. Use Add line below.'}
        </p>
      ) : (
        // V3-031 / TBL-19: w-0 + min-w-full keeps the table's width out of the page's layout, so
        // this box (not the page) is what scrolls sideways and the sticky Amount column stays
        // pinned at its right edge. With w-full the page grew to the table's width and Amount
        // ended up past the screen edge.
        <div className="tw-w-0 tw-min-w-full tw-overflow-x-auto" data-slot="he-grid-scroll">
          <Table hover size="sm" className="tw-mb-0">
            <thead>
              <tr>
                <th className={STICKY_NUM}>#</th>
                <th className={STICKY_DATE}>Date*</th>
                <th className={LONG_TEXT_COL}>Vendor*</th>
                <th className="tw-min-w-[7rem]">PO</th>
                {/* V2-033: in By Task mode the task is fixed (it is the entry's anchor), so the column is
                    capped and the full name is in the cell's title. Otherwise its width was the full
                    task label and pushed Qty, Rate and Fuel off screen. */}
                <th className={locks.rowTaskSelectable ? 'tw-min-w-[13rem]' : TASK_LOCKED_COL}>Task*</th>
                <th className={LONG_TEXT_COL}>Resource*</th>
                <th className="tw-min-w-[5.5rem]">Qty*</th>
                <th className="tw-min-w-[3.5rem]">Unit</th>
                <th className="tw-min-w-[5.5rem]">Rate*</th>
                {showFuel && (
                  <th className="!tw-overflow-visible tw-w-[8.5rem] tw-min-w-[8.5rem]">
                    {headerTooltip(FUEL_SURCHARGE_TOOLTIP, 'he-fuel-col', FUEL_SURCHARGE_COLUMN)}
                  </th>
                )}
                <th className="!tw-overflow-visible tw-min-w-[5rem]">
                  {headerTooltip(INTERNAL_MATERIALS_TOOLTIP, 'he-int-mat-col', INTERNAL_MATERIALS_COLUMN)}
                </th>
                <th className="!tw-overflow-visible tw-min-w-[5.5rem]">
                  {headerTooltip(OVERTIME_ELIGIBLE_TOOLTIP, 'he-ot-col', OVERTIME_ELIGIBLE_COLUMN)}
                </th>
                <th className="tw-w-12 tw-min-w-[3rem] tw-text-center" aria-label="Line comment">
                  <i className="bi bi-chat-left" aria-hidden="true" />
                </th>
                {showTicketColumn && <th className="tw-min-w-[6.5rem]">Ticket</th>}
                {showAttachmentColumn && <th className="tw-min-w-[7rem]">Attachment</th>}
                <th className={STICKY_AMOUNT}>Amount</th>
                {!readOnly && <th className={`${STICKY_ACTIONS} tw-text-right`}> </th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const fuelPerHour = fuelSurchargePerHourForLine(fuelSurcharges, row);
                const tableFuelPerHour = lookupFuelSurchargePerHour(fuelSurcharges, row.vehicleType, row.date);
                const fuelOverridden =
                  row.fuelSurchargeEligible === true && row.fuelSurchargeOverridePerHour != null;
                const amount = lineAmount(row.qty, row.rate, row.overtimeEligible, row.unit, fuelPerHour);
                const otBreakdown = formatHeOvertimeBreakdownText({
                  qty: row.qty,
                  rate: row.rate,
                  overtimeEligible: row.overtimeEligible,
                  unit: row.unit,
                  fuelPerHour,
                });
                const otToggle = canToggleHeOvertime(row.resourceOvertimeEligible, row.unit);
                const resourceOptions = resourcesForRow(row);
                const vendorPos = filterPosForTask(
                  posForVendor(purchaseOrders, row.vendorId),
                  locks.rowTaskSelectable ? row.taskId : (anchor.lockedTaskId ?? row.taskId),
                  tasksByPoId,
                );
                if (row.poId != null && !vendorPos.some((po) => po.id === row.poId)) {
                  const current = purchaseOrders.find((po) => po.id === row.poId);
                  if (current) vendorPos.push(current);
                }
                const lineNumber = index + 1;
                const rowIncomplete = attemptedSubmit && rowHasMissingRequired(row);
                const cloning = cloningClientIds?.has(row.clientId) ?? false;
                // C3 / H8: value problems show inline as soon as they are typed, not only on submit.
                const qtyMessage = quantityProblem(row);
                const fuelMessage = fuelOverrideProblem(row);
                const rateWindowText = row.rateWindow ? formatRateWindow(row.rateWindow) : '';
                const rowBg = row.duplicatedPending ? 'tw-bg-content/20' : undefined;
                return (
                  <React.Fragment key={row.clientId}>
                  <tr
                    id={gridLineElementId(lineNumber)}
                    data-incomplete={rowIncomplete ? 'true' : undefined}
                    data-duplicated-pending={row.duplicatedPending ? 'true' : undefined}
                    title={row.duplicatedPending ? 'Duplicated line – edit any field to confirm it' : undefined}
                    // Table's own `hover` prop targets `tbody tr:hover` with higher specificity
                    // than a per-row hover: utility, so it wins on hover regardless - a plain
                    // background here is all that reliably shows. /20 rather than /5: most of
                    // a row's area is occluded by each cell's own white input box, so a subtle
                    // tint only shows in the cell padding and reads as barely-there.
                    className={rowBg}
                  >
                    <td className={`${STICKY_NUM} tw-text-content-muted`}>{lineNumber}</td>
                    <Td invalid={invalid(row, 'date')} message={message(row, 'date')} className={STICKY_DATE}>
                      {readOnly ? (
                        <span className="tw-whitespace-nowrap tw-text-sm tw-tabular-nums tw-text-content" aria-label={`Date, line ${index + 1}`}>
                          {formatDate(row.date) || <EmptyValue />}
                        </span>
                      ) : (
                      <Form.Control
                        type="date"
                        aria-label={`Date, line ${index + 1}`}
                        value={row.date}
                        className={cellClass(invalid(row, 'date'))}
                        onChange={(e) => patch(row.clientId, { date: e.target.value })}
                      />
                      )}
                    </Td>
                    <Td invalid={invalid(row, 'vendor')} message={message(row, 'vendor')}>
                      {locks.rowVendorSelectable ? (
                        <Form.Select
                          aria-label={`Vendor, line ${index + 1}`}
                          value={row.vendorId ?? ''}
                          className={`${cellClass(invalid(row, 'vendor'))} ${LONG_TEXT_VALUE}`}
                          title={row.vendorName ?? undefined}
                          onChange={(e) => handleVendorChange(row, e.target.value ? Number(e.target.value) : '')}
                        >
                          <option value="">Select vendor...</option>
                          {vendors.map((vendor) => (
                            <option key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <LockedText title={row.vendorName ?? undefined} className={LONG_TEXT_VALUE}>{row.vendorName}</LockedText>
                      )}
                    </Td>
                    <td>
                      {locks.rowPoSelectable ? (
                        <Form.Select
                          aria-label={`Purchase order, line ${index + 1}`}
                          value={row.poId ?? ''}
                          disabled={!row.vendorId && !anchor.lockedVendorId}
                          className={cellClass(false)}
                          onChange={(e) => handlePoChange(row, e.target.value ? Number(e.target.value) : '')}
                        >
                          <option value="">{row.vendorId ? 'Select PO...' : 'Pick a vendor first'}</option>
                          {vendorPos.map((po) => (
                            <option key={po.id} value={po.id}>
                              {formatPoOptionLabel(po)}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <LockedText title={row.poNumber ?? undefined}>{row.poNumber}</LockedText>
                      )}
                    </td>
                    <Td invalid={invalid(row, 'task')} message={message(row, 'task')} className={locks.rowTaskSelectable ? '' : TASK_LOCKED_COL}>
                      {locks.rowTaskSelectable ? (
                        <HiredEquipmentTaskPicker
                          selected={taskOptionForRow(row)}
                          onChange={(task) => handleTaskChange(row, task)}
                          searchTasks={searchTasks}
                          scopedOptions={scopedTasksForPoRow(
                            row,
                            tasksByPoId,
                            resourceOptions,
                            taskOptionForRow(row),
                          )}
                          invalid={invalid(row, 'task')}
                          onCreateTask={onCreateTask ? () => onCreateTask(row) : undefined}
                          onViewTask={onViewTask ? (task) => onViewTask(task, row) : undefined}
                        />
                      ) : (
                        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1">
                          <LockedText title={row.taskLabel ?? undefined}>{row.taskLabel}</LockedText>
                          {onViewTask && lockedTaskOption(row) && (
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="tw-shrink-0 tw-px-1.5"
                              title="View task details"
                              disabled={readOnly}
                              aria-label={`View task ${row.taskLabel ?? row.taskId}`}
                              onClick={() => onViewTask(lockedTaskOption(row)!, row)}
                            >
                              <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                            </Button>
                          )}
                        </span>
                      )}
                    </Td>
                    <Td invalid={invalid(row, 'resource') || invalid(row, 'description')} message={message(row, 'resource') || message(row, 'description')}>
                      {readOnly ? (
                        <LockedText title={(row.lumpSum ? row.lumpSumDescription : row.resourceLabel) ?? undefined}>
                          {row.lumpSum
                            ? row.lumpSumDescription?.trim()
                              ? `${LUMP_SUM_RESOURCE_LABEL} – ${row.lumpSumDescription.trim()}`
                              : LUMP_SUM_RESOURCE_LABEL
                            : row.resourceLabel}
                        </LockedText>
                      ) : row.lumpSum ? (
                        // M7: a lump sum says what it pays for.
                        <Form.Control
                          type="text"
                          aria-label={`Lump sum description, line ${index + 1}`}
                          placeholder="Lump sum – description*"
                          maxLength={HE_LUMP_SUM_DESCRIPTION_MAX}
                          value={row.lumpSumDescription ?? ''}
                          className={`${cellClass(invalid(row, 'description'))} ${LONG_TEXT_VALUE}`}
                          onChange={(e) => patch(row.clientId, { lumpSumDescription: e.target.value })}
                        />
                      ) : (
                        <Form.Select
                          aria-label={`Resource, line ${index + 1}`}
                          value={row.resourceId ?? ''}
                          className={`${cellClass(invalid(row, 'resource'))} ${LONG_TEXT_VALUE}`}
                          title={row.resourceLabel ?? undefined}
                          onChange={(e) =>
                            handleResourceChange(row, e.target.value ? Number(e.target.value) : '', resourceOptions)
                          }
                        >
                          <option value="">Select resource...</option>
                          <ResourceSelectOptions options={resourceOptions} />
                        </Form.Select>
                      )}
                      {/* H4: shown as text, not a hover tooltip, so it reads on touch too. A
                          warning only – the line can still be saved and submitted. */}
                      {row.complianceWarnings && row.complianceWarnings.length > 0 && (
                        <p
                          className="tw-mb-0 tw-mt-0.5 tw-flex tw-items-start tw-gap-1 tw-text-xs tw-text-warning-text"
                          data-testid={`he-compliance-${lineNumber}`}
                        >
                          <i className="bi bi-exclamation-triangle-fill tw-mt-0.5" aria-hidden="true" />
                          <span>{row.complianceWarnings.join(' ')}</span>
                        </p>
                      )}
                    </Td>
                    <Td invalid={invalid(row, 'qty') || qtyMessage != null} message={qtyMessage ? null : message(row, 'qty')}>
                      {row.lumpSum ? (
                        <LockedText>
                          <span aria-label={`Quantity, line ${index + 1}`}>–</span>
                        </LockedText>
                      ) : readOnly ? (
                        <span className="tw-block tw-text-right tw-text-sm tw-tabular-nums tw-text-content" aria-label={`Quantity, line ${index + 1}`}>
                          {row.qty ?? <EmptyValue />}
                        </span>
                      ) : (
                        <>
                          <Form.Control
                            type="text"
                            inputMode={qtyStepForUnit(row.unit) === '1' ? 'numeric' : 'decimal'}
                            aria-label={`Quantity, line ${index + 1}`}
                            aria-invalid={qtyMessage != null || undefined}
                            aria-describedby={qtyMessage ? `he-qty-msg-${lineNumber}` : undefined}
                            value={qtyDraft[row.clientId] ?? (row.qty ?? '')}
                            className={`${cellClass(invalid(row, 'qty') || qtyMessage != null)} tw-w-20 tw-text-right tw-tabular-nums`}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setQtyDraft((prev) => ({ ...prev, [row.clientId]: raw }));
                              patch(row.clientId, { qty: parseNumber(raw) });
                            }}
                            onBlur={() =>
                              setQtyDraft((prev) => {
                                const next = { ...prev };
                                delete next[row.clientId];
                                return next;
                              })
                            }
                          />
                          {qtyMessage && (
                            <p id={`he-qty-msg-${lineNumber}`} role="alert" className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-danger">
                              {qtyMessage}
                            </p>
                          )}
                        </>
                      )}
                    </Td>
                    <td>
                      <LockedText>
                        {/* M7: no stray "$" unit on a lump sum line. */}
                        <span aria-label={`Unit, line ${index + 1}`}>{row.lumpSum ? '–' : row.unit || '–'}</span>
                      </LockedText>
                    </td>
                    <Td invalid={!row.lumpSum && (invalid(row, 'rate') || row.noRateOnDate === true)}>
                      {row.lumpSum ? (
                        <LockedText>
                          <span aria-label={`Rate, line ${index + 1}`}>–</span>
                        </LockedText>
                      ) : row.noRateOnDate ? (
                        <span aria-label={`Rate, line ${index + 1}`} className="tw-block tw-text-xs tw-text-danger">
                          {/* V2-050: with no task there is nothing to look a rate up for yet. */}
                          {row.taskId == null ? 'Pick a task to look up the rate.' : 'No approved rate on this date'}
                        </span>
                      ) : (
                        <MoneyHover
                          id={`he-rate-window-${lineNumber}`}
                          text={
                            row.rate != null && rateWindowText
                              ? row.rateWindow?.basis === 'component-sum'
                                ? 'Bundle – sum of its components’ rates on this date'
                                : `${formatCurrency(row.rate)} in effect ${rateWindowText}`
                              : null
                          }
                        >
                          <LockedText>
                            <span aria-label={`Rate, line ${index + 1}`}>
                              {row.rate == null ? '–' : formatCurrency(row.rate)}
                            </span>
                          </LockedText>
                        </MoneyHover>
                      )}
                    </Td>
                    {showFuel && (
                      <Td invalid={fuelMessage != null} className="tw-w-[8.5rem] tw-min-w-[8.5rem] tw-whitespace-nowrap">
                        {row.lumpSum ? (
                          <span
                            aria-label={`Fuel surcharge, line ${index + 1}`}
                            className="tw-text-sm tw-text-content-muted"
                          >
                            –
                          </span>
                        ) : (
                        // M28: fixed two-slot layout – the checkbox never moves and the cell never
                        // changes width when it is toggled.
                        <div className="tw-grid tw-grid-cols-[1.25rem_1fr] tw-items-center tw-gap-1.5">
                          <span
                            className="tw-inline-flex tw-justify-center"
                            title={
                              row.fuelSurchargeEligible == null ? FUEL_SURCHARGE_UNCONFIGURED : FUEL_SURCHARGE_ARIA
                            }
                          >
                            {readOnly ? (
                              <span className="tw-text-sm tw-text-content" aria-label={`${FUEL_SURCHARGE_ARIA}, line ${index + 1}`}>
                                {row.fuelSurchargeEligible === true ? 'Yes' : 'No'}
                              </span>
                            ) : (
                            <Form.Check
                              aria-label={`${FUEL_SURCHARGE_ARIA}, line ${index + 1}`}
                              checked={row.fuelSurchargeEligible === true}
                              onChange={(e) => {
                                const checked = e.currentTarget.checked;
                                // Unchecking clears any override, so the line carries no fuel at all.
                                patch(row.clientId, {
                                  fuelSurchargeEligible: checked,
                                  ...(checked ? {} : { fuelSurchargeOverridePerHour: null }),
                                });
                              }}
                            />
                            )}
                          </span>
                          {canOverrideFuel && row.fuelSurchargeEligible === true && !readOnly ? (
                            <div className="tw-min-w-0">
                              <div className="tw-flex tw-items-center tw-gap-0.5">
                                <span className="tw-text-sm tw-text-content-muted" aria-hidden="true">
                                  $
                                </span>
                                <Form.Control
                                  type="text"
                                  inputMode="decimal"
                                  aria-label={`Fuel surcharge override, line ${index + 1}`}
                                  aria-invalid={fuelMessage != null || undefined}
                                  title={FUEL_SURCHARGE_OVERRIDE_TOOLTIP}
                                  // No default 0: empty means "use the fuels table", shown as the placeholder.
                                  placeholder={tableFuelPerHour != null ? tableFuelPerHour.toFixed(2) : ''}
                                  value={fuelDraft[row.clientId] ?? (row.fuelSurchargeOverridePerHour ?? '')}
                                  className={`${cellClass(fuelMessage != null)} tw-w-16 tw-text-right tw-tabular-nums`}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    setFuelDraft((prev) => ({ ...prev, [row.clientId]: raw }));
                                    patch(row.clientId, {
                                      fuelSurchargeOverridePerHour: raw.trim() === '' ? null : parseNumber(raw),
                                    });
                                  }}
                                  onBlur={() =>
                                    setFuelDraft((prev) => {
                                      const next = { ...prev };
                                      delete next[row.clientId];
                                      return next;
                                    })
                                  }
                                />
                              </div>
                              {fuelMessage && (
                                <p role="alert" className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-danger">
                                  {fuelMessage}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span
                              aria-label={`Fuel surcharge, line ${index + 1}`}
                              className={`tw-text-sm tw-tabular-nums ${
                                fuelOverridden ? 'tw-rounded tw-bg-warning tw-px-1 tw-font-semibold tw-text-warning-text' : ''
                              }`}
                              // H8: an override is flagged to the approver with the standard rate beside it.
                              title={
                                fuelOverridden
                                  ? `Override ${formatCurrency(row.fuelSurchargeOverridePerHour!)}/h – standard ${
                                      tableFuelPerHour != null ? `${formatCurrency(tableFuelPerHour)}/h` : 'not set'
                                    }`
                                  : undefined
                              }
                            >
                              {row.fuelSurchargeEligible === true ? formatCurrency(fuelPerHour) : '–'}
                              {fuelOverridden && (
                                <span className="tw-ml-1 tw-text-xs tw-font-normal">
                                  (std {tableFuelPerHour != null ? formatCurrency(tableFuelPerHour) : '–'})
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        )}
                      </Td>
                    )}
                    <td className="tw-text-center">
                      <span className="tw-relative tw-inline-flex">
                        {readOnly ? (
                          <span className="tw-text-sm tw-text-content" aria-label={`${INTERNAL_MATERIALS_ARIA}, line ${index + 1}`}>
                            {row.vendorMaterial ? 'Yes' : 'No'}
                          </span>
                        ) : (
                        <Form.Check
                          aria-label={`${INTERNAL_MATERIALS_ARIA}, line ${index + 1}`}
                          checked={row.vendorMaterial}
                          onChange={(e) => handleVendorMaterialChange(row, e.currentTarget.checked)}
                        />
                        )}
                        <IntMatCheckFlag flagged={row.vendorMaterial} />
                      </span>
                    </td>
                    <td className="tw-text-center">
                      {otToggle && readOnly ? (
                        <span className="tw-text-sm tw-text-content" aria-label={`${OVERTIME_ELIGIBLE_ARIA}, line ${index + 1}`}>
                          {row.overtimeEligible ? 'Yes' : 'No'}
                        </span>
                      ) : otToggle ? (
                        <Form.Check
                          aria-label={`${OVERTIME_ELIGIBLE_ARIA}, line ${index + 1}`}
                          checked={row.overtimeEligible}
                          onChange={(e) =>
                            patch(row.clientId, { overtimeEligible: e.currentTarget.checked })
                          }
                        />
                      ) : (
                        <span className="tw-text-content-muted" aria-hidden="true">
                          –
                        </span>
                      )}
                    </td>
                    <td className="tw-text-center">
                      <LineCommentCell
                        lineNumber={lineNumber}
                        comments={row.lineComments}
                        canComment={canComment}
                        onAdd={(text) =>
                          patch(row.clientId, {
                            lineComments: [...(row.lineComments ?? []), { text, authorName: null, at: null }],
                          })
                        }
                      />
                    </td>
                    {showTicketColumn && (
                    <td className="tw-w-28">
                      {readOnly ? (
                        <span className="tw-text-sm tw-text-content">{row.ticketNumber?.trim() || <EmptyValue />}</span>
                      ) : (
                        <Form.Control
                          type="text"
                          aria-label={`Ticket number, line ${index + 1}`}
                          placeholder="Optional"
                          value={row.ticketNumber ?? ''}
                          className={cellClass(false)}
                          onChange={(e) => patch(row.clientId, { ticketNumber: e.target.value })}
                        />
                      )}
                    </td>
                    )}
                    {showAttachmentColumn && (
                    <td>
                      <RowAttachmentCell
                        attachment={row.attachment}
                        lineNumber={lineNumber}
                        onChange={(attachment) => patch(row.clientId, { attachment })}
                      />
                    </td>
                    )}
                    {row.lumpSum && !readOnly ? (
                      <Td invalid={invalid(row, 'rate')} message={message(row, 'rate')} className={STICKY_AMOUNT}>
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          aria-label={`Amount, line ${index + 1}`}
                          value={amountDraft[row.clientId] ?? (row.rate ?? '')}
                          className={`${cellClass(invalid(row, 'rate'))} tw-w-28 tw-text-right tw-tabular-nums`}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setAmountDraft((prev) => ({ ...prev, [row.clientId]: raw }));
                            patch(row.clientId, { rate: parseNumber(raw) });
                          }}
                          onBlur={() =>
                            setAmountDraft((prev) => {
                              const next = { ...prev };
                              delete next[row.clientId];
                              return next;
                            })
                          }
                        />
                      </Td>
                    ) : (
                    <td className={`${STICKY_AMOUNT} tw-whitespace-nowrap tw-text-sm tw-tabular-nums`}>
                      <MoneyHover text={otBreakdown} id={`he-amount-ot-${index + 1}`}>
                        {formatCurrency(amount)}
                      </MoneyHover>
                    </td>
                    )}
                    {!readOnly && (
                    <td className={`${STICKY_ACTIONS} tw-text-right`}>
                      <RowActions
                        maxInline={2}
                        actions={[
                          {
                            kind: 'custom',
                            icon: cloning ? 'bi-hourglass-split' : 'bi-copy',
                            label: 'Duplicate',
                            variant: 'outline-secondary',
                            disabled: cloning,
                            onClick: () => onDuplicateLine(row.clientId),
                          },
                          {
                            kind: 'delete',
                            onClick: () => onRemoveLine(row.clientId),
                          },
                        ]}
                      />
                    </td>
                    )}
                  </tr>
                  {row.vendorMaterial && (
                    <tr className="tw-bg-primary/10 [&:hover]:tw-bg-primary/10">
                      <td colSpan={columnCount} className="tw-px-3 tw-py-2">
                        <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-3">
                          <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-rounded-full tw-bg-primary tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white">
                            {INTERNAL_MATERIALS_BADGE}
                          </span>
                          {readOnly ? (
                            <span className="tw-min-w-0 tw-flex-1 tw-text-sm tw-text-content" aria-label={`${INTERNAL_MATERIALS_ARIA} notes, line ${lineNumber}`}>
                              {row.vendorMaterialNotes?.trim() || <EmptyValue />}
                            </span>
                          ) : (
                          <Form.Control
                            aria-label={`${INTERNAL_MATERIALS_ARIA} notes, line ${lineNumber}`}
                            placeholder={INTERNAL_MATERIALS_NOTES_PLACEHOLDER}
                            value={row.vendorMaterialNotes ?? ''}
                            className={`${cellClass(false)} tw-min-w-[12rem] tw-flex-1`}
                            onChange={(e) =>
                              patch(row.clientId, { vendorMaterialNotes: e.target.value })
                            }
                          />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="tw-bg-content tw-text-white [&_td]:tw-bg-content">
                <td colSpan={footerLeadColSpan} className="tw-px-3 tw-py-2 tw-text-sm tw-text-white">
                  {rows.length} line{rows.length === 1 ? '' : 's'}{incompleteCount > 0 ? ` · ${incompleteCount} incomplete` : ''} · Totals
                </td>
                <td className={`${STICKY_AMOUNT} !tw-bg-content tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-sm tw-text-white tw-tabular-nums`}>
                  <MoneyHover text={footerOtText} id="he-footer-amount-ot">
                    {formatCurrency(totalAmount)}
                  </MoneyHover>
                </td>
                {footerTrailColSpan > 0 && (
                  <td colSpan={footerTrailColSpan} className={`${STICKY_ACTIONS} !tw-bg-content`} aria-hidden="true" />
                )}
              </tr>
            </tfoot>
          </Table>
        </div>
      )}
      </div>

      {!readOnly && (
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-line tw-px-4 tw-py-3">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          <Button variant="outline-primary" size="sm" onClick={onAddLine}>
            <i className="bi bi-plus-lg" />
            Add line
          </Button>
          {onAddLumpSumLine && (
            <InfoTooltip content={LUMP_SUM_ADD_TOOLTIP} id="he-add-lump-sum">
              <Button variant="outline-secondary" size="sm" onClick={onAddLumpSumLine}>
                <i className="bi bi-cash" />
                {LUMP_SUM_ADD_LABEL}
              </Button>
            </InfoTooltip>
          )}
          {showReAdd && (
            <button
              type="button"
              onClick={onReAddPoResources}
              className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-text-primary hover:tw-underline"
            >
              Re-add all PO resources
            </button>
          )}
        </div>
        <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
          Tip: use Duplicate to repeat a line on a new date – no pop-ups, edit inline like a spreadsheet.
        </p>
      </div>
      )}
    </div>
  );
};

export default HiredEquipmentGrid;
